import {makeAutoObservable, runInAction} from "mobx";
import config from "../../../config";
import CustomersService, {ICustomer, ICustomerRmasList} from "../../../services/api/customer-service";
import {rootContainer, TYPE} from "../../../services/container/root";
export const _CUSTOMER_EMPTY_ID_ = '_CUSTOMER_EMPTY_ID_';

class CustomerStore {
	private _model?: ICustomer;
    private _rmasList?: ICustomerRmasList;
	private _isLoadingRmas: boolean = false;
    private _isLoading = false; 
    /** this is TRUE when we have customer id but server did not not find the customer on platform, we still might have records for him in our DB like rmas list */
    private _isNotFound = false; 
    /** this is TRUE when there is NO customer id at all - so we know there is not customer to search for */
    private _isNotExists = false;
    /** this is TRUE when the model was fetched without the shopper profile fields (name, admin link) */
    private _isProfileExcluded = false;
	
	constructor(private readonly _id: string) {
        /** the passed id is the id by witch we fetch customer from server (should be same as the _model?.denormalized_id), 
         * we need it to be able to load rma list without waiting for customer model from server */
		makeAutoObservable(this);
	}

    public setModel(customer?: ICustomer, isProfileExcluded = false) {
        this._model = customer;
        this._isProfileExcluded = isProfileExcluded;
    }

    public get isProfileExcluded(): boolean { return this._isProfileExcluded; }
	
	private get service(): CustomersService { return rootContainer.get<CustomersService>(TYPE.CustomersService); }
	public get isLoadingRmas(): boolean { return this._isLoadingRmas; }
	public get fullName(): string | undefined {	
        if (!this._model?.firstName && !this._model?.lastName) {
            return undefined;
        }
        return `${this._model?.firstName || ""} ${this._model?.lastName || ""}`.trim();
    }
    public get customerURL(): string { return this._model?.customerURL ?? '' }
	public get email(): string | undefined { return this._model?.email; }
    public get denormalizedId(): string { return (this._model?.denormalized_id ?? '')}
    public getCreditBalance(conversionRate?: number): number { return ((this._model?.credits ?? 0) / (conversionRate ?? 1)) ; }
    public get isLoading(): boolean { return this._isLoading; }
    public setIsLoading(v: boolean): void { this._isLoading = v; }

    public get isNotFound(): boolean { return this._isNotFound; }
    public setNotFound(): void { this._isNotFound = true; }

    public get isNotExists(): boolean { return this._isNotExists; }
    public setNotExists(): void { this._isNotExists = true; }

    public getBalanceLink(): string {
        return config.apiUrl + '/dashboard/analytics/credits?search_customer_id=' + this._model?.denormalized_id;
    }

    public getTransactionsLink(): string {
        return config.apiUrl + '/dashboard/analytics/transactions?filter_customer_id=' + encodeURIComponent(this._model?.denormalized_id ?? '');
    }

	public async loadRmasList(){
        if (this._id === _CUSTOMER_EMPTY_ID_) {
            this._rmasList = {
                total_count: 0,
                list: [],
            };
        }

        if (this._rmasList || this._isLoadingRmas) {
            /** do not request when in loading process or when loaded or when no cutomer id */
            return;
        }

		try {
            this._isLoadingRmas = true;
            const rmasList = await this.service.fetchCustomerRmaList(this._id);
            runInAction(() => {
                this._isLoadingRmas = false;
                this._rmasList = rmasList;
            })
        }
        catch (e) {
            runInAction(() => {
                this._isLoadingRmas = false;
            });
        }
	}
	
    public getRmas() { return this._rmasList; }
}

export default CustomerStore;